import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { format } from "date-fns";
import { type ReactElement } from "react";
import { type Control, Controller, type FieldValues } from "react-hook-form";
import type { FieldPath } from "react-hook-form/dist/types";
import { type ReuseUiData } from "@/types/acquirebook";

interface ReuseInformationRadioButtonsProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  reuseData: ReuseUiData;
}

// 識別子から画面に表示する文字列に変換する
const reuseOptionToString = (reuseOption: string): string => {
  switch (reuseOption) {
    case "REUSE_NEW":
      return "新規";
    case "REUSE_OWN":
      return "取得済登記";
    case "REUSE_TRUSTART":
      return "リユース登記";
  }
  return "";
};

// 識別子に応じて日時をフォーマットして文字列に変換する
const printDate = (
  reuseOption: string,
  acquiredDate: string,
  postfix: string | undefined
): string => {
  switch (reuseOption) {
    case "REUSE_OWN":
      return `（${format(new Date(acquiredDate), "yyyy/MM/dd")}${
        postfix ?? ""
      }）`;
    case "REUSE_TRUSTART":
      return `（${format(new Date(acquiredDate), "yyyy/MM/dd")}${
        postfix ?? ""
      }）`;
  }
  return "";
};

/**
 * 登記/図面を再利用するためのラジオボタンを表示するコンポーネント
 * @param props
 * control: reactHookFormのcontrolを渡す
 * name: reactHookForm上でのオブジェクト名
 * reuseData: バックエンドから取得したUI用に加工したデータ
 * disabled: ラジオボタンを表示するか非表示にするか決めるフラグ
 */
const ReuseInformationRadioButtons = <T extends FieldValues>(
  props: ReuseInformationRadioButtonsProps<T>
): ReactElement | null => {
  const { reuseData, control, name } = props;

  return (
    <FormControl>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          return (
            <RadioGroup row={true} {...field}>
              {reuseData.reuseNew && (
                <FormControlLabel
                  value={"REUSE_NEW"}
                  control={
                    <Radio
                      sx={{ py: 0 }}
                      disabled={reuseData.reuseNew.disabled}
                    />
                  } // ラジオボタンの高さを小さくしてレイアウトを揃える
                  label={reuseOptionToString("REUSE_NEW")}
                />
              )}
              {reuseData.reuseOwn && (
                <FormControlLabel
                  value={"REUSE_OWN"}
                  control={
                    <Radio
                      sx={{ py: 0 }}
                      disabled={reuseData.reuseOwn.disabled}
                    />
                  } // ラジオボタンの高さを小さくしてレイアウトを揃える
                  label={`${reuseOptionToString("REUSE_OWN")} ${printDate(
                    "REUSE_OWN",
                    reuseData.reuseOwn.acquiredDate,
                    "取得"
                  )}`}
                />
              )}
              {reuseData.reuseTrustart && (
                <FormControlLabel
                  value={"REUSE_TRUSTART"}
                  control={
                    <Radio
                      sx={{ py: 0 }}
                      disabled={reuseData.reuseTrustart.disabled}
                    />
                  } // ラジオボタンの高さを小さくしてレイアウトを揃える
                  label={`${reuseOptionToString("REUSE_TRUSTART")} ${printDate(
                    "REUSE_TRUSTART",
                    reuseData.reuseTrustart.acquiredDate,
                    "取得"
                  )}`}
                />
              )}
            </RadioGroup>
          );
        }}
      />
    </FormControl>
  );
};

export { ReuseInformationRadioButtons };
